// Aspect-ratio lock helpers for element width/height. The lock state lives on
// the element as `aspectLocked` (boolean) and persists in the element JSON.
// When the field is absent, defaultAspectLocked(type) provides the per-type
// default so existing designs behave sensibly.

const LOCKED_BY_DEFAULT = new Set(['image', 'svg', 'frame']);

export function defaultAspectLocked(type) {
  return LOCKED_BY_DEFAULT.has(type);
}

// Resolve the effective lock state: explicit field wins, else per-type default.
export function isAspectLocked(element) {
  if (!element) return false;
  if (typeof element.aspectLocked === 'boolean') return element.aspectLocked;
  return defaultAspectLocked(element.type);
}

// Compute the new { width, height } when one dimension changes. When locked,
// the other dimension scales to preserve the element's current ratio.
export function resizeKeepingAspect(element, dim, rawValue, locked) {
  const value = Math.max(1, Number(rawValue) || 0);
  const w = Math.max(1, Number(element.width) || 1);
  const h = Math.max(1, Number(element.height) || 1);
  if (!locked) {
    return dim === 'width' ? { width: value, height: h } : { width: w, height: value };
  }
  const ratio = w / h; // width / height
  if (dim === 'width') {
    return { width: value, height: Math.max(1, Math.round(value / ratio)) };
  }
  return { width: Math.max(1, Math.round(value * ratio)), height: value };
}

// Constrain a free drag-resize box to the original aspect ratio. Returns the
// adjusted { width, height } using the dominant axis of the drag so the box
// tracks the pointer naturally (mirrors typical design-tool behavior).
export function constrainResizeToAspect(origWidth, origHeight, newWidth, newHeight) {
  const ow = Math.max(1, Number(origWidth) || 1);
  const oh = Math.max(1, Number(origHeight) || 1);
  const ratio = ow / oh;
  const w = Math.max(1, Number(newWidth) || 1);
  const h = Math.max(1, Number(newHeight) || 1);
  // Use whichever axis changed more (relative to original) as the driver.
  const dw = Math.abs(w - ow) / ow;
  const dh = Math.abs(h - oh) / oh;
  if (dw >= dh) {
    return { width: w, height: Math.max(1, w / ratio) };
  }
  return { width: Math.max(1, h * ratio), height: h };
}
